import React, { useState, useEffect, useRef } from 'react';
import { 
  FaTrash, 
  FaPlus, 
  FaParagraph, 
  FaListUl, 
  FaTable, 
  FaGripVertical,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaEdit,
  FaInfoCircle,
  FaPencilAlt, FaTimes
} from 'react-icons/fa';
import Card from '../../../components/Card/Card';
import Button from '../../../components/buttons/Button';
import './steps.css';

function StepSectionEditor({ 
  sections, 
  onSectionUpdate, 
  onSectionReorder, 
  onSectionDelete, 
  onSectionAdd 
}) {
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [newSectionId, setNewSectionId] = useState(null);
  const [showFormatSelector, setShowFormatSelector] = useState(false);
  const sectionListRef = useRef(null);
  
  // Scroll to new section when added
  useEffect(() => {
    if (newSectionId && sectionListRef.current) {
      const newSectionElement = document.getElementById(`section-${newSectionId}`);
      if (newSectionElement) {
        newSectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Flash animation to highlight new section
        newSectionElement.classList.add('new-section-highlight');
        setTimeout(() => {
          newSectionElement.classList.remove('new-section-highlight');
          setNewSectionId(null);
        }, 1500);
      }
    }
  }, [newSectionId]);

  // Format icons mapping - reordered
  const formatOrder = ['inline', 'paragraph', 'list', 'table'];
  const formatIcons = {
    inline: <FaAlignLeft />,
    paragraph: <FaParagraph />,
    list: <FaListUl />,
    table: <FaTable />
  };

  const formatLabels = {
    inline: 'En ligne',
    paragraph: 'Paragraphe',
    list: 'Liste',
    table: 'Tableau'
  };

  // Handle drag start
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverItem(index);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Handle drop
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedItem !== null && draggedItem !== dropIndex) {
      onSectionReorder(draggedItem, dropIndex);
    }
    handleDragEnd();
  };


  // Change section format
  const changeFormat = (sectionId, format) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    
    // Inline sections cannot change format
    if (section.format === 'inline') return;
    
    // Table sections cannot change format
    if (section.format === 'table') return;
    
    // Only allow paragraph <-> list changes
    if ((section.format === 'paragraph' && format === 'list') ||
        (section.format === 'list' && format === 'paragraph')) {
      onSectionUpdate(sectionId, { format });
    }
  };
  
  // Change section alignment
  const changeAlignment = (sectionId, alignment) => {
    onSectionUpdate(sectionId, { alignment });
  };

  // Get available formats for a section
  const getAvailableFormats = (section) => {
    // Inline sections are locked
    if (section.format === 'inline') return ['inline'];
    
    // Table sections are locked
    if (section.format === 'table') return ['table'];
    
    // Paragraph and list can switch between each other
    if (section.format === 'paragraph') return ['paragraph', 'list'];
    if (section.format === 'list') return ['list', 'paragraph'];
    
    return ['paragraph', 'list'];
  };

  // Start editing section
  const startEditing = (section) => {
    setEditingSection({
      id: section.id,
      title: section.title,
      subtitle: section.subtitle || '',
      alignment: section.alignment || (section.format === 'table' ? 'center' : 'left')
    });
  };

  // Save edited section
  const saveEditing = () => {
    if (editingSection) {
      onSectionUpdate(editingSection.id, {
        title: editingSection.title,
        subtitle: editingSection.subtitle,
        alignment: editingSection.alignment
      });
      setEditingSection(null);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingSection(null);
  };

  // Update editing values
  const updateEditingValue = (field, value) => {
    setEditingSection(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get preview text for section
  const getSectionPreview = (section) => {
    const content = section.content || '';
    const maxLength = 100;
    
    if (section.format === 'table') {
      const lines = content.split('\n').filter(l => l.trim());
      return lines.length > 0 ? `${lines.length} ligne(s) de tableau` : 'Tableau vide';
    }
    
    if (section.format === 'list') {
      const items = content.split('\n').filter(l => l.trim());
      return `${items.length} élément(s)`;
    }
    
    if (content.length > maxLength) {
      return content.substring(0, maxLength) + '...';
    }
    
    return content || 'Contenu vide';
  };

  return (
    <div className="step-section-editor">
      {/* Header with Add Button */}
      <div className="step-editor-header">
        <h3>Sections du template</h3>
        <div className="step-add-section-container">
          {!showFormatSelector ? (
            <Button
              variant="primary"
              size="small"
              icon={FaPlus}
              onClick={() => setShowFormatSelector(true)}
            >
              Ajouter une section
            </Button>
          ) : (
            <div className="step-format-selector-new">
              <span className="step-format-selector-label">Choisir le format:</span>
              <button
                className="step-format-option"
                onClick={() => {
                  onSectionAdd('inline');
                  setShowFormatSelector(false);
                  setTimeout(() => {
                    const allSections = document.querySelectorAll('.step-section-card');
                    if (allSections.length > 0) {
                      const lastSection = allSections[allSections.length - 1];
                      lastSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      lastSection.classList.add('new-section-highlight');
                      setTimeout(() => {
                        lastSection.classList.remove('new-section-highlight');
                      }, 1500);
                    }
                  }, 100);
                }}
                title="Ligne"
              >
                <FaAlignLeft /> Ligne
              </button>
              <button
                className="step-format-option"
                onClick={() => {
                  onSectionAdd('paragraph');
                  setShowFormatSelector(false);
                  setTimeout(() => {
                    const allSections = document.querySelectorAll('.step-section-card');
                    if (allSections.length > 0) {
                      const lastSection = allSections[allSections.length - 1];
                      lastSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      lastSection.classList.add('new-section-highlight');
                      setTimeout(() => {
                        lastSection.classList.remove('new-section-highlight');
                      }, 1500);
                    }
                  }, 100);
                }}
                title="Paragraphe"
              >
                <FaParagraph /> Paragraphe
              </button>
              <button
                className="step-format-option"
                onClick={() => {
                  onSectionAdd('list');
                  setShowFormatSelector(false);
                  setTimeout(() => {
                    const allSections = document.querySelectorAll('.step-section-card');
                    if (allSections.length > 0) {
                      const lastSection = allSections[allSections.length - 1];
                      lastSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      lastSection.classList.add('new-section-highlight');
                      setTimeout(() => {
                        lastSection.classList.remove('new-section-highlight');
                      }, 1500);
                    }
                  }, 100);
                }}
                title="Liste"
              >
                <FaListUl /> Liste
              </button>
              <button
                className="step-format-option"
                onClick={() => {
                  onSectionAdd('table');
                  setShowFormatSelector(false);
                  setTimeout(() => {
                    const allSections = document.querySelectorAll('.step-section-card');
                    if (allSections.length > 0) {
                      const lastSection = allSections[allSections.length - 1];
                      lastSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      lastSection.classList.add('new-section-highlight');
                      setTimeout(() => {
                        lastSection.classList.remove('new-section-highlight');
                      }, 1500);
                    }
                  }, 100);
                }}
                title="Tableau"
              >
                <FaTable /> Tableau
              </button>
              <button
                className="step-format-option cancel"
                onClick={() => setShowFormatSelector(false)}
                title="Annuler"
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Combined Info & Tips */}
      <div className="step-editor-tips">
        <h4><FaInfoCircle /> Conseils</h4>
        <p style={{ marginBottom: '12px' }}>Réorganisez, modifiez ou supprimez les sections pour créer votre template parfait.</p>
        <ul>
          <li>Chaque section sera rempli automatiquement par votre IA pour ce modèle</li>
          <li>Glissez-déposez les sections pour les réorganiser et choisir le format approprié (paragraphe, liste, etc.)</li>
          <li>Les sections "en ligne" sont Titre: contenu; les paragraphes sont Titre (à la ligne) contenu.</li>
        </ul>
      </div>

      {/* Sections List */}
      {sections.length === 0 ? (
        <div className="tbw-empty-state">
          <FaPencilAlt className="tbw-empty-state-icon" />
          <div className="tbw-empty-state-title">Aucune section</div>
          <div className="tbw-empty-state-message">
            Ajoutez des sections pour construire votre template.
          </div>
        </div>
      ) : (
        <div className="step-sections-list" ref={sectionListRef}>
          {sections.map((section, index) => (
            <Card
              key={section.id}
              id={`section-${section.id}`}
              className={`step-section-card ${
                dragOverItem === index ? 'drag-over' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              onDrop={(e) => handleDrop(e, index)}
            >
              {/* Drag Handle */}
              <div className="step-section-drag">
                <FaGripVertical />
              </div>

              {/* Section Content */}
              <div className="step-section-content">
                {editingSection?.id === section.id ? (
                  // Edit Mode
                  <div className="step-section-edit">
                    <input
                      type="text"
                      className="step-section-edit-title"
                      value={editingSection.title}
                      onChange={(e) => updateEditingValue('title', e.target.value)}
                      placeholder="Titre de la section"
                      autoFocus
                    />
                    {(sections.find(s => s.id === section.id)?.format === 'paragraph' || 
                      sections.find(s => s.id === section.id)?.format === 'list') && (
                      <input
                        type="text"
                        className="step-section-edit-subtitle"
                        value={editingSection.subtitle}
                        onChange={(e) => updateEditingValue('subtitle', e.target.value)}
                        placeholder="Sous-titre (optionnel)"
                      />
                    )}
                    <div className="step-section-edit-alignment">
                      <label>Alignement:</label>
                      <div className="step-alignment-buttons">
                        <button
                          type="button"
                          className={`step-alignment-btn ${editingSection.alignment === 'left' ? 'active' : ''}`}
                          onClick={() => updateEditingValue('alignment', 'left')}
                          title="Aligner à gauche"
                        >
                          <FaAlignLeft />
                        </button>
                        <button
                          type="button"
                          className={`step-alignment-btn ${editingSection.alignment === 'center' ? 'active' : ''}`}
                          onClick={() => updateEditingValue('alignment', 'center')}
                          title="Centrer"
                        >
                          <FaAlignCenter />
                        </button>
                        <button
                          type="button"
                          className={`step-alignment-btn ${editingSection.alignment === 'right' ? 'active' : ''}`}
                          onClick={() => updateEditingValue('alignment', 'right')}
                          title="Aligner à droite"
                        >
                          <FaAlignRight />
                        </button>
                      </div>
                    </div>
                    <div className="step-section-edit-actions">
                      <Button
                        variant="success"
                        size="small"
                        onClick={saveEditing}
                      >
                        Enregistrer
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={cancelEditing}
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <>
                    <div className="step-section-header">
                      <h4 className="step-section-title">
                        {section.title}
                      </h4>
                      {/* Alignment Buttons - Next to Title */}
                      <div className="step-section-alignment-selector">
                        <button
                          className={`step-alignment-btn ${(section.alignment || 'left') === 'left' ? 'active' : ''}`}
                          onClick={() => changeAlignment(section.id, 'left')}
                          title="Aligner à gauche"
                        >
                          <FaAlignLeft />
                        </button>
                        <button
                          className={`step-alignment-btn ${section.alignment === 'center' ? 'active' : ''}`}
                          onClick={() => changeAlignment(section.id, 'center')}
                          title="Centrer"
                        >
                          <FaAlignCenter />
                        </button>
                        <button
                          className={`step-alignment-btn ${section.alignment === 'right' ? 'active' : ''}`}
                          onClick={() => changeAlignment(section.id, 'right')}
                          title="Aligner à droite"
                        >
                          <FaAlignRight />
                        </button>
                      </div>
                      <div className="step-section-meta">
                        <span className="step-section-format">
                          {formatIcons[section.format]} {formatLabels[section.format]}
                        </span>
                      </div>
                    </div>
                    <div className="step-section-preview">
                      {getSectionPreview(section)}
                    </div>
                  </>
                )}
              </div>

              {/* Section Actions */}
              {editingSection?.id !== section.id && (
                <div className="step-section-actions">
                  {/* Format Selector */}
                  <div className="step-section-format-selector">
                    {getAvailableFormats(section).map(format => (
                      <button
                        key={format}
                        className={`step-format-btn ${section.format === format ? 'active' : ''}`}
                        onClick={() => changeFormat(section.id, format)}
                        title={formatLabels[format]}
                        disabled={getAvailableFormats(section).length === 1}
                      >
                        {formatIcons[format]}
                      </button>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="step-section-buttons">
                    <button
                      className="step-action-btn"
                      onClick={() => startEditing(section)}
                      title="Éditer"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="step-action-btn danger"
                      onClick={() => onSectionDelete(section.id)}
                      title="Supprimer"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default StepSectionEditor;